// Hooks import statements
import { useState } from "react";

// External import statements
import { Editor } from "@monaco-editor/react";

// Mui components import statements
import {
  Box,
  Button,
  IconButton,
  Tab,
  Tabs,
  tabsClasses,
  Typography,
} from "@mui/material";

// Mui icons import statements
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import { Save as SaveIcon } from "@mui/icons-material";

// Function that returns Tab panel with custom styles
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      className="flex-container-column"
      hidden={value !== index}
      id={`code-editor-tabpanel-${index}`}
      aria-labelledby={`code-editor-tab-${index}`}
      {...other}
      style={{ height: "100%", display: value === index ? "flex" : "none" }}
    >
      {value === index && (
        <Box sx={{ height: "100%", borderRadius: "8px", overflow: "hidden" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Function that returns properties of tab panels with index
function a11yProps(index) {
  return {
    id: `code-editor-tab-${index}`,
    "aria-controls": `code-editor-tabpanel-${index}`,
  };
}

const MultipleCodeEditor = ({ editorsData, setEditorsData, language }) => {
  const [localEditorsData, setLocalEditorsdata] = useState(editorsData);
  const [copied, setCopied] = useState(false);

  // Controls current tab index
  const [currentTab, setCurrentTab] = useState(0);

  // function to handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Function to remove files or tab data from editorsData
  const handleCloseTab = (indexToDelete) => {
    setLocalEditorsdata((prevData) =>
      prevData.filter((_, index) => index !== indexToDelete)
    );
  };

  const handleCopy = () => {
    navigator.clipboard.writeText((Object.values(localEditorsData[currentTab])[0]).replace(/\\n/g, "\n"));

    // Change icon state
    setCopied(true);

    // Revert after 2 seconds
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }

  return (
    <Box
      className="flex-container-column"
      height={"100%"}
      width={"100%"}
      sx={{
        borderRadius: "8px",
        padding: "24px",
        gap: "16px",
        overflow: "hidden", // Prevent any overflow
        boxSizing: "border-box", // Include padding in height calculation
      }}
    >
      {/* Header Box - Fixed height */}
      <Box
        className="flex-container"
        sx={{
          alignItems: "center",
          flexShrink: 0, // Prevent shrinking
          height: "40px", // Set a minimum height for consistency
        }}
      >
        <Typography fontSize={"14px"}>Click to edit</Typography>
        <Box sx={{display: "flex", gap: "5px"}}>
          <Button
            onClick={handleCopy}
            endIcon={copied ? <CheckCircleRoundedIcon sx={{ width: 17, height: 20, color: "#4CAF50" }} /> : <ContentCopyRoundedIcon sx={{ width: "17px", height: "20px" }} />}
            sx={{
              color: "#49A3FF",
              fontSize: "14px",
              lineHeight: "24px",
              gap: "8px",
              alignItems: "center",
              textTransform: "none"
            }}
          >
              {copied ? "Copied to clipboard" : "Copy to clipboard"}
          </Button>
          {JSON.stringify(editorsData[currentTab]) !==
            JSON.stringify(localEditorsData[currentTab]) && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => setEditorsData(localEditorsData)}
              size="small"
              sx={{
                backgroundColor: "#0094FB",
                "&:hover": {
                  backgroundColor: "#0078D4",
                },
              }}
            >
              Save
            </Button>
          )}
        </Box>
      </Box>

      <Box
        className="flex-container-column"
        sx={{
          flex: 1,
          minHeight: 0,
          gap: "10px",
        }}
      >
        {/* Tabs Box - Fixed height */}
        <Box
          sx={{
            // flexShrink: 0, // Prevent shrinking
            height: "56px", // Typical height for Material-UI tabs
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            sx={{
              [`& .${tabsClasses.scrollButtons}`]: {
                "&.Mui-disabled": { opacity: 0.3, color: "black" },
                width: "56px",
                height: "56px",

                "& svg": {
                  fontSize: "2rem",
                },

                color: "#0094FB",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#0094FB",
              },
              height: "56px",
            }}
          >
            {localEditorsData.map((eachEditor, index) => (
              <Tab
                key={index}
                label={
                  <Box className="flex-container-row" sx={{ gap: "16px" }}>
                    <Typography sx={{ fontSize: "18px" }}>
                      {Object.keys(eachEditor)[0]}
                    </Typography>
                    <IconButton
                      sx={{ height: "24px", width: "24px" }}
                      onClick={() => handleCloseTab(index)}
                    >
                      <CloseIcon sx={{ color: "#0094FB" }} />
                    </IconButton>
                  </Box>
                }
                {...a11yProps(index)}
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  "&.Mui-selected": {
                    color: "#0094FB",
                    backgroundColor: "#E1F5FF",
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Editor Box - Takes remaining space */}
        <Box
          className="flex-container-column"
          sx={{
            flex: 1,
            minHeight: 0, // Allow shrinking below content size
            overflow: "hidden", // Prevent overflow
          }}
        >
          {localEditorsData.map((eachEditorData, index) => (
            <CustomTabPanel
              className="flex-container-column"
              key={index}
              value={currentTab}
              index={index}
              sx={{
                height: "100%",
              }}
            >
              <Box
                className="flex-container"
                sx={{
                  height: "100%",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <Editor
                  height="536px"
                  defaultLanguage={language || "python"}
                  value={Object.values(eachEditorData)[0]}
                  onChange={(newValue) => {
                    setLocalEditorsdata((prev) =>
                      prev.map((item, i) =>
                        i === index
                          ? { [Object.keys(item)[0]]: newValue } // update only changed editor
                          : item
                      )
                    );
                  }}
                  options={{
                    scrollBeyondLastLine: false,
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineHeight: 1.5,
                    lineNumbers: "on",
                    scrollbar: {
                      alwaysConsumeMouseWheel: false,
                    },
                    // Disable editor scrolling when not focused
                    mouseWheelScrollSensitivity: 0.5,
                  }}
                  beforeMount={(monaco) => {
                    monaco.editor.defineTheme("myCustomTheme", {
                      base: "vs-dark",
                      inherit: true,
                      rules: [],
                      colors: {
                        "editorGutter.background": "#333333",
                        "editorLineNumber.foreground": "#969090ff", // normal line numbers
                        "editorLineNumber.activeForeground": "#FFFFFF", // current line number
                      },
                    });
                  }}
                  theme={"myCustomTheme"}
                />
              </Box>
            </CustomTabPanel>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default MultipleCodeEditor;
